import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { Box, Palette, PaletteColor, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    DossierObjectGroup,
    dossierOpenSearchObjectGroupSelector,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/pro-solid-svg-icons';
import { AnyAction } from '@reduxjs/toolkit';

import { useDossierObjectGroup } from '@europrocurement/l2d-modules/src/modules/Dossiers/hooks';
import ActionMenuListeDossierGroup from '@europrocurement/l2d-modules/src/modules/Dossiers/components/liste/ActionMenuListeDossierGroup/ActionMenuListeDossierGroup';

export const DossierGroupTable: React.FunctionComponent = function () {
    const dispatch = useDispatch();

    const datasource: DataSource<DossierObjectGroup> = useSelector(
        dossierOpenSearchObjectGroupSelector,
    );
    const { getDossierOpenSearchObjectGroups } = useFormaliteService();

    // filter
    const { searchDossierObjectGroup } = useDossierObjectGroup();
    const { palette } = useTheme();

    const filters: FiltersDatatableList = [
        {
            label: 'Groupes',
            field: 'groupId',
            type: 'selectApi',
            icon: 'folders',
            searchFunction: searchDossierObjectGroup,
            renderItem: (item: DossierObjectGroup) => item.label as string,
            renderIcon: (item: DossierObjectGroup, defaultIcon) => {
                const icon = item?.icon || defaultIcon;
                const color = item?.color;

                return (
                    <FaOptionIcon
                        sx={{
                            fontSize: '12px',
                            lineHeight: '0',
                            color: color ? `${color}.main` : 'inherit',
                        }}
                        icon={['fasl', icon as IconName]}
                    />
                );
            },
            valueMap: (item: DossierObjectGroup) => item.id as number,
        },
    ];

    const columns: ColumnDatatable<DossierObjectGroup>[] = [
        {
            label: 'Groupe',
            render: ({ label, color = 'primary' }) => {
                const iconColor = color && color !== '' ? color : 'primary';
                const paletteColor = palette[iconColor as keyof Palette] as PaletteColor;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FaOptionIcon
                            icon={['fasl', 'folders']}
                            color={paletteColor.main}
                        />
                        <Typography>{label}</Typography>
                    </Box>
                );
            },
            displayLabel: false,
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Nombre de dossiers',
            render: ({ childsCount }) => (
                <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%', gap: '5px' }}>
                    <Typography>
                        {`${childsCount} ${childsCount && childsCount > 1 ? ' dossiers' : 'dossier'}`}
                    </Typography>
                </Box>
            ),
            isDisplayed: true,
            sortable: false,
            displayLabel: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
                textAlign: 'end',
            },
        },
        {
            label: 'Actions',
            displayLabel: false,
            render: (dossierGroupe: DossierObjectGroup) => (
                <ActionMenuListeDossierGroup dossierGroupe={dossierGroupe} />
            ),
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'flex',
                    lg: 'flex',
                    xl: 'flex',
                },
                justifyContent: 'end',
            },
        },
    ];

    useEffect(() => {
        dispatch(getDossierOpenSearchObjectGroups({}) as unknown as AnyAction);
    }, [getDossierOpenSearchObjectGroups, dispatch]);

    return (
        <StoreDatatable
            dataSource={datasource}
            columns={columns}
            hideColumnOptions
            fetchData={getDossierOpenSearchObjectGroups}
            localStorageKey="FlexyStoreDatatable"
            filters={filters}
        />
    );
};
